import { useEffect, useRef, useState } from "react";
import { dashboardApi } from "../../../../services/dashboardApi";

const DETAIL_APIS = {
  productionDashboard: "getProductionDashboard",
  machineUtilizationCompleteData: "getMachineUtilizationCompleteData",
  operatorUtilization: "getOperatorUtilization",
};

const CARD_DETAILS = {
  PRODUCTION: ["productionDashboard"],
  MACHINE: ["machineUtilizationCompleteData"],
  OPERATOR: ["operatorUtilization"],
};

export default function useDashboardDetails({
  selectedCard,
  machineOperatorEnabled,
  filters,
  refreshVersion,
  selectionVersion,
}) {
  const scope = JSON.stringify([filters, refreshVersion]);
  const cacheRef = useRef({ scope: null, requests: new Map() });
  const [state, setState] = useState({ data: {}, pending: [], failures: [] });

  useEffect(() => {
    const keys = selectedCard === "PRODUCTION" || machineOperatorEnabled
      ? CARD_DETAILS[selectedCard] : null;
    if (!keys) return;

    if (cacheRef.current.scope !== scope) {
      cacheRef.current = { scope, requests: new Map() };
    }
    const cache = cacheRef.current.requests;
    let active = true;
    setState((previous) => ({
      scope,
      selectedCard,
      data: previous.scope === scope ? previous.data : {},
      pending: keys,
      failures: [],
    }));

    keys.forEach((key) => {
      if (!cache.has(key)) {
        const request = Promise.resolve()
          .then(() => dashboardApi[DETAIL_APIS[key]](filters))
          .then((response) => {
            if (response?.data == null) {
              throw new Error("Unable to load dashboard details.");
            }
            return response.data;
          });
        cache.set(key, request);
        // Failed requests can be retried by clicking the card again.
        request.catch(() => cache.delete(key));
      }

      cache.get(key).then(
        (data) => {
          if (!active) return;
          setState((previous) => ({
            ...previous,
            data: { ...previous.data, [key]: data },
            pending: previous.pending.filter((item) => item !== key),
          }));
        },
        () => {
          if (!active) return;
          setState((previous) => ({
            ...previous,
            failures: [...previous.failures, key],
            pending: previous.pending.filter((item) => item !== key),
          }));
        },
      );
    });

    return () => { active = false; };
  }, [machineOperatorEnabled, selectedCard, filters, scope, selectionVersion]);

  const visible = selectedCard === "PRODUCTION" ||
    (machineOperatorEnabled && Boolean(CARD_DETAILS[selectedCard]));
  const current = state.scope === scope;
  return {
    data: visible && current ? state.data : {},
    loading: visible && (
      !current || state.selectedCard !== selectedCard || state.pending.length > 0
    ),
    failedKeys: visible && current ? state.failures : [],
  };
}
